import {
  CGV2LocationOutDTO,
  ExpeditionTypeEnum,
  InvoiceStateEnum,
  LabelPreviewWithoutTypeOutDTO,
  LovDTO, NotifyTargetInnerInDTO, PolandSentRequest,
  SaveExpeditionLocationRequestInDTO, SupplierNotificationPreviewOutDTO,
  TransportationTypeEnum,
  UpdateExpeditionIncotermsRequest,
} from '@api/ringil-api.v2.tsx';
import { UploadedAttachment } from '@components/ringil3/Features/Attachments/UploadAttachmentDialog.tsx';
import { CargoType } from '@components/framework/input/InputCargoType/InputCargoType.tsx';
import { RebillingType } from '@components/framework/tags/InternalInfoRebillingTag.tsx';
import {
  CarrierDistributionSelectorData,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';

interface ExpeditionLocation extends CGV2LocationOutDTO {}

export enum LocalityEntryModeEnum {
  MANUALLY = 'MANUALLY',
  FROM_LOCATIONS = 'FROM_LOCATIONS',
}

export interface ManualAddress {
  name?: string;
  postalCode?: string;
  countryCode3?: string;
  city?: string;
  streetNr?: string;
  mainContactPerson?: string;
  mainContactPhone?: string;
  mainContactEmail?: string;
  notifySelectedPerson?: boolean;
  additionalPersons?: AdditionalPerson[];
  timezoneId?: string;
}

export interface ExpeditionLocality extends SaveExpeditionLocationRequestInDTO {
  localityMode: LocalityEntryModeEnum;
  location: ExpeditionLocation;
  manualAddress: ManualAddress;
  additionalPersons?: AdditionalPerson[];
  // this is using for animation
  id: string;
  /**
   * Set by the prefill when this stop's location had been superseded and was swapped for its
   * replacement: the name of the location the source shipment actually used. Display only — it marks
   * the swap for the user and is never sent (mapLocation builds the request field by field).
   */
  supersededFromLocationName?: string;
  /**
   * Set by the prefill from a Linka (Template 2.0): the line stop this locality came from. Its weekly
   * windows (opening hours of entity type TMPL_V2_STOP) fill the picked days instead of the location's.
   */
  tmplV2StopId?: string;
  /** True when the line stop has weekly windows of its own; without them the location's opening hours apply. */
  tmplV2StopHasOpeningHours?: boolean;
  /** The line stop's own note for the carrier; replaces the address-book note of whichever location gets picked. */
  tmplV2StopLocationNote?: string;
  /**
   * How freely the location may be changed — set only by the Linka prefill:
   * LOCKED = the single candidate location, read-only; LIMITED = pick one of `allowedLocationIds`;
   * FREE / undefined = the normal behaviour (any address-book location or a manual address).
   */
  locationLock?: LocationLockMode;
  /** The candidate locations of a LIMITED stop. */
  allowedLocationIds?: string[];
}

export type LocationLockMode = 'LOCKED' | 'LIMITED' | 'FREE';

export interface AdditionalPerson {
  name: string;
  email: string;
}

export interface NewExpeditionForm {
  locations: ExpeditionLocality[];
  distribution: CarrierDistributionSelectorData;
  specialRequirements?: string[];
  carrierNote?: string;
  transportationType?: TransportationTypeEnum;
  expeditionType?: ExpeditionTypeEnum;
  loadingTypes?: string[];
  truckTypeCode?: string;
  cargoType?: CargoType;
  persons?: LovDTO[];
  person?: LovDTO;
  /** RD-2977: an expedition can be filed in several folders */
  folders?: LovDTO[];
  notifyByRole?: boolean;
  invoiceState?: InvoiceStateEnum;
  disponentPerson?: LovDTO;
  pairingSymbol?: string;
  internalNote?: string;
  rebilling?: RebillingType;
  attachments?: UploadedAttachment[];
  customApplicationId?: string;
  name?: string;
  labels?: LabelPreviewWithoutTypeOutDTO[];
  incoterms?: UpdateExpeditionIncotermsRequest;
  customsDeclarationCompanies?: LovDTO[];
  internalInfoAccountCodeListId?: string;
  internalInfoCostCenterCodeListId?: string;
  internalInfoCostUnitCodeListId?: string;
  internalInfoProjectCodeListId?: string;
  huEkaerNumber?: string;
  roUitNumber?: string;
  utnCode?: string;
  polandSent?: PolandSentRequest;
  notifiedTargets?: NotifyTargetInnerInDTO[];
  supplierNotificationLink?: SupplierNotificationPreviewOutDTO;
  /** Hidden link to the Linka (Template 2.0) the form was prefilled from; sent on save. */
  tmplV2Id?: string;
  /** Display only: the name of that Linka, for the banner and the contracted-prices sidebar. */
  tmplV2Name?: string;
  /** The Linka's capacity (laden factor), always offered in the laden-factor select even when it does not divide the unit count. */
  defaultLadenFactor?: number;
  /** A both-direction Linka: its stops as prefilled, so picking a location of the last stop turns the route around. */
  lineRoute?: LineRoute;
}

/** One stop of the Linka the form was prefilled from - the location-bound part, which travels with the stop when the route turns. */
export interface LineRouteStop {
  tmplV2StopId: string;
  tmplV2StopHasOpeningHours: boolean;
  loadingReference?: string;
  requirements?: string[];
  locationNote?: string;
  /** Current address-book data of the stop's candidate locations; 0 = any, 1 = locked, N = pick one. */
  candidates: CGV2LocationOutDTO[];
}

export interface LineRoute {
  /** The stops in the Linka's own order. */
  stops: LineRouteStop[];
  /** The form currently drives the route from the Linka's last stop to its first one. */
  reversed: boolean;
}
